import React, { useMemo, useState } from 'react';
import { Input } from 'antd';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import {
  ALL_EXCLUDED, ALL_EXCLUDED_AO3, ALL_EXCLUDED_RR, PODIUM_TITLES, PODIUM_TITLES_AO3, PODIUM_TITLES_RR,
  SEND_TO_CRM, SENT_TO_CRM_AO3, SENT_TO_CRM_RR, SOURCE
} from '../../../core/constants';
import { IBook } from '../../../section/Common/types/types';
import { useActions } from '../../../context/Actions.context';
import showActionNotification from '../../../section/Common/utils/showActionNotification';

const { TextArea } = Input;

interface IProps {
  book: IBook | Record<string, string | number>;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const getPodiumData = (source: string, book: IBook | Record<string, string | number>) => {
  const podiumData = {
    [SOURCE.bookstat]: {
      podiumTitleObject: { amazonasin: book.ASIN },
      podiumTitleType: PODIUM_TITLES,
      excludeType: ALL_EXCLUDED
    },
    [SOURCE['royal-road']]: {
      podiumTitleObject: { fiction_id: book['Fiction Id'] },
      podiumTitleType: PODIUM_TITLES_RR,
      excludeType: ALL_EXCLUDED_RR
    },
    [SOURCE.ao3]: {
      podiumTitleObject: { work_id: book.work_id },
      podiumTitleType: PODIUM_TITLES_AO3,
      excludeType: ALL_EXCLUDED_AO3
    }
  };

  return podiumData[source];
};

const PodiumTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { source, isActionLoading, setActionLoading } = useActions();
  const queryClient = useQueryClient();
  const history = useHistory();
  const podiumData = useMemo(() => getPodiumData(source?.['source'], book), [source, book]);
  const [notes, setNotes] = useState<string>('');

  const { mutateAsync: mutatePodium } = useUpdateTitle(podiumData?.podiumTitleType);
  const { mutateAsync: mutateExclude } = useUpdateTitle(podiumData?.excludeType);

  const handleClose = () => {
    setNotes('');
    toggleModal();
  };

  const onSend = () => {
    if (!podiumData) {
      showActionNotification({
        type: 'error',
        message: 'Unable to determine the target for this action.'
      });
      return;
    }
    setActionLoading(true);

    const body = {
      ...(notes.trim() && { notes: notes.trim() })
    };

    const capturedSource = source?.['source'];

    const promise = mutatePodium({ params: podiumData.podiumTitleObject, body });

    promise.then(res => {
      if (res?.status === 200) {
        showActionNotification({
          type: 'success',
          message: 'The Title was successfully sent to the Podium Titles tab.',
          destination: {
            name: 'Podium Titles',
            onGoTo: () => history.push(`${capturedSource}/podium-titles`)
          }
        });
      } else {
        showActionNotification({
          type: 'error',
          message: 'The Title has not been sent to the Podium Titles tab.'
        });
      }
    }).catch(() => {
      showActionNotification({
        type: 'error',
        message: 'The Title has not been sent to the Podium Titles tab.'
      });
    });

    promise.then(() => {
      if (id === SEND_TO_CRM || id === SENT_TO_CRM_RR || id === SENT_TO_CRM_AO3) {
        showActionNotification({ type: 'info', message: 'Please delete the data from Hubspot.' });
      }
    });

    if (id === ALL_EXCLUDED || id === ALL_EXCLUDED_RR || id === ALL_EXCLUDED_AO3) {
      promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
      promise.then(() => queryClient.refetchQueries(`get-${id}`));
      promise.finally(() => setActionLoading(false));
      handleClose();
      return;
    }

    mutateExclude({ params: podiumData.podiumTitleObject })
      .then(excludeRes => {
        if (excludeRes?.status === 200) {
          showActionNotification({
            type: 'success',
            message: 'The Title was successfully sent to the Excluded tab.'
          });
        }
        queryClient.refetchQueries(`get-row-count${id}`);
        queryClient.refetchQueries(`get-${id}`);
      })
      .catch(() => {
        showActionNotification({
          type: 'error',
          message: 'The Title has not been sent to the Podium Titles tab.'
        });
        setActionLoading(false);
      })
      .finally(() => {
        setActionLoading(false);
        handleClose();
      });
  };

  if (!podiumData) {
    return null;
  }

  return (
    <Modal
      okText={'Send To Podium Titles'}
      title='Send To Podium Titles'
      visible={modal}
      onOk={onSend}
      confirmLoading={isActionLoading}
      onCancel={handleClose}
    >
      <div>
        <span>
          Help yourself and others understand why you&apos;re sending this. Leave a note, or skip and
          continue.
        </span>
        <br />
        <br />
        <TextArea
          placeholder='Add your notes here...'
          maxLength={400}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />

        <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
          {notes.length} / 400
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(PodiumTitles);